import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { NgDynamicFormComponent } from '@tesla/ng-dynamic-form';
import { INgDynamicFormJson } from '@tesla/ng-dynamic-form/lib/models/ng-dynamic-form.model';
import { DataWithSupplierCode, EmailDomain, EmailDomainPropertyValue, OPERATION, SupplierDocument } from 'app/profile/models/supplier.model';
import { OnboardingDocumentsService } from 'app/profile/services/onboarding-document.service';
import { I18nService } from 'app/shared/services/i18n.service';
import * as FileSaver from 'file-saver';
import { TranslocoService } from '@ngneat/transloco';
import { ReplaySubject, Subscription } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import _ from 'lodash';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { isNullOrEmpty, isNullOrUndefined } from 'app/shared/utils/utils';
import { MasterDataService } from 'app/profile/services/masterdata.service';
import { TslDialog } from '@tesla/ng-tds';

@Component({
  selector: 'app-domain-edit',
  templateUrl: './domain-edit.component.html',
  styleUrls: ['./domain-edit.component.scss']
})
export class DomainEditComponent implements OnInit, OnDestroy {
  @ViewChild('domainForm', { static: false }) public domainForm: NgDynamicFormComponent;
  @Input('tsl-initialValue') emailDomainValues: EmailDomain;
  @Input('tsl-readOnly') readOnly = false;
  @Input('tsl-supplierCode') supplierCode: string = '';
  @Input('tsl-hideSaveButton') hideSaveBtn: boolean = false;
  @Input('tsl-buttonLabel') saveButtonLabel: string = "Submit";
  @Input('tsl-existingDomains') existingDomains: string[];
  @Input('tsl-domainDifferenceReasons') reasons: string[] = [];
  @Output('tsl-onSave') onSubmitEvent: EventEmitter<{ emailDomain: EmailDomain }> = new EventEmitter<{ emailDomain: EmailDomain }>();
  @Output('tsl-onSaveError') onErrorEvent: EventEmitter<{ errorMessage: string }> = new EventEmitter<{ errorMessage: string }>();

  formSchema: INgDynamicFormJson;
  formData: any = {};
  translateFile;
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  subscription = new Subscription();
  domainReasons: string[] = [];
  file = {};

  constructor(
    private translate: I18nService,
    private masterDataService: MasterDataService,
    private onboardingDocumentsService: OnboardingDocumentsService,
    private translocoService: TranslocoService,
    public dialogRef: MatDialogRef<DomainEditComponent>,
    private dialog: TslDialog,
    @Inject(MAT_DIALOG_DATA) public matData: DataWithSupplierCode<EmailDomainPropertyValue>,
  ) { }

  ngOnInit(): void {
    this.initTranslation();
    this.initFormData();
    this.masterDataService.getEmailDomainReasons().subscribe(res => {
      if(res){
        this.domainReasons = res;
        if(this.matData && this.matData.data && this.matData.data.reason && this.matData.data.reason.newValue){
          if(!this.domainReasons.includes(this.matData.data.reason.newValue)){
            this.domainReasons.push(this.matData.data.reason.newValue);
          }
        }
        this.initFormSchema();
      }
    });
    if(this.matData && this.matData.suppliercode && !isNullOrUndefined(this.matData.data) && !isNullOrEmpty(this.matData.data)){
      this.emailDomainValues = new EmailDomain();
      this.emailDomainValues.domain = this.matData.data.domain?.newValue;
      this.emailDomainValues.reason = this.matData.data.reason?.newValue;
      this.emailDomainValues.comment = this.matData.data.comment?.newValue;
      this.emailDomainValues.additionalReason = this.matData.data.additionalReason?.newValue;
      this.emailDomainValues.website = this.matData.data.website?.newValue;
      if(this.matData.data.id && this.matData.data?.id?.newValue){
        this.emailDomainValues.id = this.matData.data.id?.newValue;
        this.emailDomainValues.operation = OPERATION.UPDATE;
      }
    }
    this.initFormData();
  }

  onNoClick(){
    if(this.matData && this.matData.data?.disableDomainEdit?.newValue == true){
      this.dialog.confirm('Confirmation', 'All your pending contact changes will be discarded?')
            .afterClosed().subscribe((response: boolean) => {
              if (response) {
                this.dialogRef.close(null);
              }
            });
    }else{
      this.dialogRef.close(null);
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
    this.subscription?.unsubscribe();
  }

  initTranslation() {
    this.destroyed$.next(true);
    this.translocoService.langChanges$.subscribe((res) => {
      if (_.isEmpty(this.translocoService.getTranslation(res))) {
          this.subscription = this.translocoService.events$
              .pipe(
                  filter((e) => e.type === 'translationLoadSuccess'),
                  takeUntil(this.destroyed$),
              )
              .subscribe((obj) => {
                  this.updateLocale(res);
              });
      } else {
          this.updateLocale(res);
      }
    });
  }

  updateLocale(newLanguage) {
    const newFile = this.translocoService.getTranslation(newLanguage);
    if (!newFile) {
        return;
    }
    this.file = newFile;
  }

  initFormSchema() {
    this.formSchema = this.getDomainFormSchema();
  }

  getDomainFormSchema(): INgDynamicFormJson | any {
    return {
      formHeader: null,
      formBody: [
        {
          sectionHeader: { displayText: "", i18nKey: "" },
          sectionDescription: null,
          isDisplayedOnPdf: true,
          isDisplayedOnUI: true,
          isCollapsable: false,
          fields: [
            {
              type: "input",
              displayName: { displayText: "Email Domain", i18nKey: "EmailDomain" },
              key: "domain",
              value: "",
              tooltip: "",
              isReadonly: this.readOnly || (this.matData && this.matData.data && this.matData.data.disableDomainEdit && this.matData.data.disableDomainEdit.newValue == true),
              isDisplayed: true,
              colSize: 6,
              props: {
                isRequired: true,
                maxWidth: "100%",
                validationFn: `(val) => {
                  return (/^@[a-zA-Z0-9.-]+/.test(val)) ? ('${this.existingDomains}'?.trim()?.split(',')?.includes(val) ? {errorMessage: '${this.translate.get("Existing domain")}'} : {isValid: true}) : {errorMessage: '${this.translate.get("Email domain format should be @ followed by the domain.")}'}
                }`,
                // fullWidth: true
              }
            },
            {
              type: "dropdown",
              displayName: { displayText: "Please provide further context on this domain difference.", i18nKey: "newdomainReason" },
              key: "reason",
              value: "",
              tooltip: "",
              isReadonly: this.readOnly,
              isDisplayed: true,
              colSize: 6,
              props: {
                isRequired: true,
                isSelectAllDisabled: true,
                initialCollectionLoading: true,
                maxWidth: "100%",
                options: this.loadReasons(),
                // contentWidth: "content",
                // fullWidth: true
              }
            },
            {
              type: "textarea",
              displayName: { displayText: "Please provide other reasons:", i18nKey: "nameChangeCommentLabel" },
              key: "additionalReason",
              value: "",
              tooltip: "",
              isReadonly: this.readOnly,
              isDisplayed: true,
              colSize: 12,
              displayedBy: '(formData) => {return formData?.reason == "Other"}',
              props: {
                isRequired: true,
                maxWidth: "100%",
                maxChar: 100,

                // The ISO-8859-1 regex is found from below blog
                // https://www.oreilly.com/library/view/regular-expressions-cookbook/9781449327453/ch04s08.html
                validationFn: `(val) => {
                  return /^[\x00-\x7F\xA0-\xFF]+$/.test(val) ? {isValid: true} : {errorMessage: '${this.translate.get("Only Latin characters are allowed")}'};
                }`,
              }
            },
            {
              type: "textarea",
              displayName: { displayText: "Please outline these services and how they connect to the business interaction with Tesla in the comments below.", i18nKey: "newdomainComment" },
              key: "comment",
              value: "",
              tooltip: "",
              isReadonly: this.readOnly,
              isDisplayed: true,
              colSize: 12,
              props: {
                isRequired: true,

                // The ISO-8859-1 regex is found from below blog
                // https://www.oreilly.com/library/view/regular-expressions-cookbook/9781449327453/ch04s08.html
                validationFn: `(val) => {
                  if (!(/^[\x00-\x7F\xA0-\xFF]+$/.test(val))) {
                    return {errorMessage: '${this.translate.get("Only Latin characters are allowed")}'};
                  }

                  return (val && val?.length >= 50) ? {isValid: true} : {errorMessage: '${this.translate.get("Please at least enter 50 characters")}'};
                }`,
                maxChar: 250
              }
            },
            {
              type: "radio-button",
              displayName: { displayText: "Is there a website linked to the domain that outlines the domain difference or how it connects to the Supplier organization?", i18nKey: "domainHasWebsite" },
              key: "hasWebsite",
              value: "",
              tooltip: "",
              isReadonly: this.readOnly,
              isDisplayed: true,
              colSize: 6,
              props: {
                isRequired: true,
                options: [
                  {
                    displayName: this.translate.get("Yes"),
                    value: true
                  },
                  {
                    displayName: this.translate.get("No"),
                    value: false
                  },
                ],
              }
            },
            {
              type: "input",
              displayName: { displayText: "Please provide the website link:", i18nKey: "domainWebsite" },
              key: "website",
              value: "",
              tooltip: "",
              isReadonly: this.readOnly,
              isDisplayed: true,
              colSize: 6,
              displayedBy: '(formData) => {return !!formData?.hasWebsite}',
              props: {
                isRequired: true,
                maxWidth: "100%",
                validationFn: (val) => (val && /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([\/\w.-]*)\/?$/.test(val + ''))? {isValid: true} : {errorMessage: this.translate.get("Invalid website")},
              }
            },
            {
              type: "file-upload",
              displayName: { displayText: "Optional - Please attach any supporting correspondence related to the domain difference", i18nKey: "domainDocument" },
              key: "document",
              description: this.translate.get('One file max size 10 MB'),
              value: "",
              tooltip: "",
              isReadonly: this.readOnly,
              isDisplayed: true,
              colSize: 12,
              props: {
                isRequired: false,
                fileUploadFormat: "multipart",
                maxFileSize: 10000000
                // fullWidth: true
              }
            },
          ]
        }
      ],
      formAction: [],
      validationFn: "",
      validationApi: {},
      availableLangs: ["en-US"]
    }
  }

  initFormData() {
    if(this.emailDomainValues) {
      let fileList;
      if(this.emailDomainValues.documentInfos?.length) {
        if(this.emailDomainValues.documentInfos[0]?.File) fileList = [this.emailDomainValues.documentInfos[0]?.File];
        else fileList = this.emailDomainValues.documentInfos.map(x => {
          if(x.FileName) x["fileName"] = x.FileName;
          return x;
        });
      }
      this.formData = {
        domain: this.emailDomainValues.domain,
        reason: this.emailDomainValues.reason,
        comment: this.emailDomainValues.comment,
        website: this.emailDomainValues.website,
        hasWebsite: this.emailDomainValues.domain && this.emailDomainValues.reason ? this.emailDomainValues.website ? true : false : null,
        additionalReason: this.emailDomainValues.additionalReason,
        document: fileList
      }
    }
  }

  loadReasons(){
    let domainChangeReasons = [];
    if(this.domainReasons && this.domainReasons.length > 0){
      this.domainReasons.forEach(x => {
        domainChangeReasons.push({
          displayName: this.translate.get(x),
          value: x
        });
      })
    }
    return domainChangeReasons;
  }

  fileActionChange(event) {
    if(event?.action == "download" && event?.file?.DocumentID && event?.file?.DocumentVersion) {
      this.onboardingDocumentsService.downloadDocument(event.file.DocumentID, this.supplierCode, event.file.DocumentVersion).subscribe(resultBlob => {
        FileSaver.saveAs(resultBlob, event.file.FileName || event.file.DocumentID);
      });
    }
  }

  submit() {
    if(this.domainForm?.dynamicFormGroup?.valid) {
      let data: any = {
        ...this.domainForm.listToObject(),
      }
      let emailDomain = new EmailDomain();
      emailDomain.comment = data.comment;
      emailDomain.domain = data.domain;
      emailDomain.reason = data.reason;
      emailDomain.additionalReason = data.additionalReason;
      emailDomain.website = data.hasWebsite ? data.website : null;
      emailDomain.stageID = this.emailDomainValues?.stageID || null;
      let doc: SupplierDocument;
      if(data.document?.length && data.document != "") {
        if(data.document[0]?.DocumentID) {
          doc = data.document[0];
        } else {
          doc = new SupplierDocument();
          doc.File = data.document[0];
          doc.FileName = data.document[0].name;
        }
      }
      emailDomain.documentInfos = doc?.FileName? [doc] : null;

      if(this.matData && this.matData.suppliercode && !isNullOrUndefined(this.matData.suppliercode) && !isNullOrEmpty(this.matData.suppliercode)){
        if(this.emailDomainValues && this.emailDomainValues.id){
          emailDomain.id = this.emailDomainValues.id;
          emailDomain.operation = this.emailDomainValues.operation;
        }else{
          emailDomain.operation = OPERATION.CREATE;
        }
        this.dialogRef.close(emailDomain);
      }else{
        this.onSubmitEvent.emit({emailDomain: emailDomain});
      }
    } else {
      this.domainForm?.dynamicFormGroup.markAllAsTouched();
      this.onErrorEvent.emit({ errorMessage: this.translate.get('Domain form is not valid.') });
    }
  }
}
